import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import Card, { CardHeader } from '../components/Card';
import DataTable, { Column } from '../components/DataTable';
import Badge, { BadgeTone } from '../components/Badge';
import Modal from '../components/Modal';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import SearchInput from '../components/SearchInput';
import Toast, { ToastMessage } from '../components/Toast';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faReceipt,
  faCircleCheck,
  faCircleXmark,
  faMoneyBillTransfer,
  faWallet,
  faPrint,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';


type TxnStatus = 'paid' | 'failed' | 'refunded' | 'pending';

interface TransactionRow {
  id: number;
  s_no: number;
  payment_id: number;
  razorpay_payment_id: string;
  razorpay_order_id: string;
  customer_name: string;
  customer_email: string;
  plan_name: string;
  payment_method: string;
  amount: number;
  status: TxnStatus;
  date: string;
  has_invoice: boolean;
  refundable: boolean;
}

interface TransactionDetail {
  payment_id: number;
  razorpay_payment_id: string;
  razorpay_order_id: string;
  customer_name: string;
  customer_email: string;
  plan_name: string;
  payment_method: string;
  amount: number;
  status: TxnStatus;
  paid_at: string | null;
  created_at: string;
  subscription_id: number | null;
  invoice: any | null;
}

// Shape returned by GET /admin/transactions/metrics/
interface TransactionMetrics {
  revenue: number;
  successful_payments: number;
  failed_payments: number;
  refunded: number;
}

interface InvoiceInfo {
  invoice_number?: string;
  invoice_date?: string;
  taxable_amount?: number;
  gst_amount?: number;
  gst_percent?: number;
  total_amount?: number;
  pdf_url?: string;
  [key: string]: any;
}

const PAGE_SIZE = 10;
const STATUS_TABS: (TxnStatus | 'all')[] = ['all', 'paid', 'pending', 'failed', 'refunded'];
const CURRENCY_SYMBOL = '₹';

const STATUS_TONE: Record<TxnStatus, BadgeTone> = {
  paid: 'green',
  pending: 'amber',
  failed: 'red',
  refunded: 'blue',
};


const LOGO_PATH = '/manna-plag-logo-horizontal.png?v=2';

function formatDateTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatAmount(amount: number) {
  return `${CURRENCY_SYMBOL}${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

const COMPANY_NAME = 'Manna Analytics Private Limited';
const COMPANY_ADDRESS = '3/343, Town Railway Nagar, Nagercoil 629001, Tamil Nadu, India';
const COMPANY_EMAIL = 'support@mannanalytics.com';
const DEFAULT_GST_PERCENT = 18;


function computeGstBreakdown(amount: number, gstPercent = DEFAULT_GST_PERCENT) {
  const total = Number(amount) || 0;
  const taxable = total / (1 + gstPercent / 100);
  const gst = total - taxable;
  return { taxable, gst, total };
}

function formatMoney(value: number) {
  return `${CURRENCY_SYMBOL}${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}
function escapeHtml(value: string) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function TransactionHistory() {
  const [txns, setTxns] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_TABS)[number]>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [metrics, setMetrics] = useState<TransactionMetrics>({ revenue: 0, successful_payments: 0, failed_payments: 0, refunded: 0 });

  // Details modal
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Invoice modal
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [invoiceDetail, setInvoiceDetail] = useState<TransactionDetail | null>(null);
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const [refundTarget, setRefundTarget] = useState<TransactionRow | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Real endpoint: GET /admin/transactions/?filter=&search=&pageNum=&pageSize=
  const loadTransactions = async () => {
    setLoading(true);

    try {
      const res = await axios.get(BaseUrl + 'admin/transactions/', {
        params: {
          filter: statusFilter === 'all' ? 'all' : statusFilter,
          search,
          pageNum: page,
          pageSize: PAGE_SIZE,
        },
      });

      const data = res.data;
      const rawResults: any[] = Array.isArray(data)
        ? data
        : (data?.results ?? []);

      const results: TransactionRow[] = rawResults.map(r => ({
        ...r,
        id: r.payment_id,
      }));

      const count = Array.isArray(data)
        ? results.length
        : (data?.count ?? results.length);

      setTxns(results);
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    } catch {
      // const fallback = FALLBACK_TRANSACTIONS.filter(
      //   t =>
      //     (statusFilter === 'all' || t.status === statusFilter) &&
      //     (!search ||
      //       t.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      //       t.customer_email.toLowerCase().includes(search.toLowerCase()) ||
      //       t.razorpay_payment_id.toLowerCase().includes(search.toLowerCase()))
      // );

      // setTxns(fallback);
      // setTotalCount(fallback.length);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadTransactions();
  }, [page, search, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    async function loadMetrics() {
      try {
        const res = await axios.get(BaseUrl + 'admin/transactions/metrics/');
        if (cancelled) return;
        const m = res.data;
        setMetrics({
          revenue: Number(m?.revenue) || 0,
          successful_payments: Number(m?.successful_payments) || 0,
          failed_payments: Number(m?.failed_payments) || 0,
          refunded: Number(m?.refunded) || 0,
        });
      } catch {
        if (cancelled) return;
        const c = { revenue: 0, successful_payments: 0, failed_payments: 0, refunded: 0 };
        txns.forEach(t => {
          if (t.status === 'paid') { c.successful_payments += 1; c.revenue += Number(t.amount) || 0; }
          if (t.status === 'failed') c.failed_payments += 1;
          if (t.status === 'refunded') c.refunded += 1;
        });
        setMetrics(c);
      }
    }
    loadMetrics();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page]);

  // Real endpoint: GET /admin/transactions/{payment_id}/
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    let cancelled = false;
    async function loadDetail() {
      setDetailLoading(true);
      setDetail(null);
      try {
        const res = await axios.get(BaseUrl + `admin/transactions/${selectedId}/`);
        if (!cancelled) setDetail(res.data);
      } catch {
        if (!cancelled) {
          const row = txns.find(t => t.payment_id === selectedId);
          if (row) {
            setDetail({
              payment_id: row.payment_id,
              razorpay_payment_id: row.razorpay_payment_id,
              razorpay_order_id: row.razorpay_order_id,
              customer_name: row.customer_name,
              customer_email: row.customer_email,
              plan_name: row.plan_name,
              payment_method: row.payment_method,
              amount: row.amount,
              status: row.status,
              paid_at: row.status === 'paid' ? row.date : null,
              created_at: row.date,
              subscription_id: null,
              invoice: null,
            });
          }
          showToast('Could not load full transaction details.', 'error');
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }
    loadDetail();
    return () => { cancelled = true; };
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real endpoints: GET /admin/transactions/{payment_id}/  +  GET /admin/transactions/{payment_id}/invoice/
  useEffect(() => {
    if (invoiceId == null) { setInvoiceDetail(null); setInvoiceInfo(null); return; }
    let cancelled = false;
    async function loadInvoice() {
      setInvoiceLoading(true);
      setInvoiceDetail(null);
      setInvoiceInfo(null);
      try {
        const [detailRes, invoiceRes] = await Promise.allSettled([
          axios.get(BaseUrl + `admin/transactions/${invoiceId}/`),
          axios.get(BaseUrl + `admin/transactions/${invoiceId}/invoice/`),
        ]);
        if (cancelled) return;
        if (detailRes.status === 'fulfilled') {
          setInvoiceDetail(detailRes.value.data);
        } else {
          const row = txns.find(t => t.payment_id === invoiceId);
          if (row) {
            setInvoiceDetail({
              payment_id: row.payment_id,
              razorpay_payment_id: row.razorpay_payment_id,
              razorpay_order_id: row.razorpay_order_id,
              customer_name: row.customer_name,
              customer_email: row.customer_email,
              plan_name: row.plan_name,
              payment_method: row.payment_method,
              amount: row.amount,
              status: row.status,
              paid_at: row.status === 'paid' ? row.date : null,
              created_at: row.date,
              subscription_id: null,
              invoice: null,
            });
          }
        }
        if (invoiceRes.status === 'fulfilled' && invoiceRes.value.data) {
          setInvoiceInfo(invoiceRes.value.data);
        }
      } finally {
        if (!cancelled) setInvoiceLoading(false);
      }
    }
    loadInvoice();
    return () => { cancelled = true; };
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const printInvoice = () => {
    if (!invoiceDetail) return;
    const t = invoiceDetail;
    const w = window.open('', '_blank');
    if (!w) {
      showToast('Please allow pop-ups to download the invoice.', 'error');
      return;
    }
    const statusLabel = t.status === 'paid' ? 'Paid' : t.status.charAt(0).toUpperCase() + t.status.slice(1);
    const logoUrl = `${window.location.origin}${LOGO_PATH}`;
    const gstPercent = invoiceInfo?.gst_percent ?? DEFAULT_GST_PERCENT;
    const computed = computeGstBreakdown(t.amount, gstPercent);
    const taxable = invoiceInfo?.taxable_amount ?? computed.taxable;
    const gst = invoiceInfo?.gst_amount ?? computed.gst;
    const total = invoiceInfo?.total_amount ?? computed.total;
    const invoiceNumber = invoiceInfo?.invoice_number ?? t.razorpay_payment_id;

    w.document.write(`
      <html><head><title>Invoice — ${escapeHtml(invoiceNumber)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: sans-serif; padding: 40px; color: #111; max-width: 760px; margin: 0 auto; font-size: 13px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
        .header img { height: 42px; width: auto; display: block; }
        .company-name { font-weight: 800; font-size: 14px; margin: 10px 0 4px; }
        .company { color: #666; font-size: 11.5px; line-height: 1.6; }
        .doc-title { font-weight: 800; font-size: 18px; letter-spacing: .02em; }
        .badge { background: #f0fdf4; color: #16a34a; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; display: inline-block; margin-top: 8px; }

        .meta-table { width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #e5e7eb; }
        .meta-table td { padding: 10px 12px; border: 1px solid #e5e7eb; vertical-align: top; }
        .meta-label { font-size: 9.5px; color: #999; text-transform: uppercase; letter-spacing: .04em; font-weight: 700; display: block; margin-bottom: 3px; }
        .meta-value { font-weight: 700; color: #111; font-size: 12.5px; }
        .meta-value.mono { font-family: monospace; font-size: 11.5px; font-weight: 600; }

        .items { width: 100%; border-collapse: collapse; margin-top: 24px; border: 1px solid #e5e7eb; }
        .items th { background: #f9fafb; font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: .04em; text-align: left; padding: 10px 12px; border: 1px solid #e5e7eb; }
        .items td { padding: 12px; border: 1px solid #e5e7eb; }
        .items th.amt, .items td.amt { text-align: right; }
        .items .sub-label td { color: #666; }
        .items .grand-row td { font-weight: 900; font-size: 14.5px; background: #f9fafb; }

        .footer-note { margin-top: 28px; color: #999; font-size: 10.5px; line-height: 1.6; border-top: 1px solid #eee; padding-top: 16px; }
      </style>
      </head><body>
        <div class="header">
          <div>
            <img src="${logoUrl}" alt="Manna Plag" onerror="this.style.display='none'" />
            <p class="company-name">${escapeHtml(COMPANY_NAME)}</p>
            <p class="company">
              ${escapeHtml(COMPANY_ADDRESS)}<br/>
              ${escapeHtml(COMPANY_EMAIL)}
            </p>
          </div>
          <div style="text-align:right">
            <p class="doc-title">TAX INVOICE</p>
            <p style="color:#666;font-size:12.5px;margin-top:4px">${escapeHtml(formatDateTime(invoiceInfo?.invoice_date ?? t.paid_at ?? t.created_at))}</p>
            <span class="badge">${escapeHtml(statusLabel)}</span>
          </div>
        </div>

        <table class="meta-table">
          <tr>
            <td style="width:50%">
              <span class="meta-label">Billed To</span>
              <span class="meta-value">${escapeHtml(t.customer_name)}</span><br/>
              <span class="meta-value" style="font-weight:500;color:#555">${escapeHtml(t.customer_email)}</span>
            </td>
            <td style="width:25%">
              <span class="meta-label">Payment ID</span>
              <span class="meta-value mono">${escapeHtml(t.razorpay_payment_id)}</span>
            </td>
            <td style="width:25%">
              <span class="meta-label">Order ID</span>
              <span class="meta-value mono">${escapeHtml(t.razorpay_order_id)}</span>
            </td>
          </tr>
        </table>

        <table class="items">
          <thead>
            <tr>
              <th style="width:6%">#</th>
              <th>Description</th>
              <th style="width:14%">Method</th>
              <th class="amt" style="width:17%">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>${escapeHtml(t.plan_name)} Plan — Subscription</td>
              <td>${escapeHtml(t.payment_method)}</td>
              <td class="amt">${escapeHtml(formatMoney(taxable))}</td>
            </tr>
            <tr class="sub-label">
              <td></td>
              <td colspan="2">Taxable Value</td>
              <td class="amt">${escapeHtml(formatMoney(taxable))}</td>
            </tr>
            <tr class="sub-label">
              <td></td>
              <td colspan="2">GST (${gstPercent}%)</td>
              <td class="amt">${escapeHtml(formatMoney(gst))}</td>
            </tr>
            <tr class="grand-row">
              <td></td>
              <td colspan="2">Total Amount</td>
              <td class="amt">${escapeHtml(formatMoney(total))}</td>
            </tr>
          </tbody>
        </table>

        <p class="footer-note">
          This is a system-generated invoice and does not require a signature.<br/>
          For queries regarding this invoice, contact ${escapeHtml(COMPANY_EMAIL)}.
        </p>

        <script>
          var img = document.querySelector('.header img');
          if (img && img.complete) { window.print(); }
          else if (img) { img.addEventListener('load', function(){ window.print(); }); img.addEventListener('error', function(){ window.print(); }); }
          else { window.print(); }
        </script>
      </body></html>
    `);
    w.document.close();
  };

  const confirmRefund = async () => {
    if (!refundTarget) return;

    setRefunding(true);

    try {
      await axios.post(
        BaseUrl + `admin/transactions/${refundTarget.payment_id}/refund/`,
        {}
      );

      await loadTransactions(); // Refresh transaction list
      showToast(`${refundTarget.razorpay_payment_id} marked as refunded.`);
    } catch {
      showToast("Refund failed.", "error");
    } finally {
      setRefunding(false);
      setRefundTarget(null);
    }
  };
  const columns: Column<TransactionRow>[] = [
    {
      key: 'payment_id', header: 'Payment ID', render: r => (
        <div>
          <p className="font-mono text-[11.5px] font-bold text-gray-800">{r.razorpay_payment_id}</p>
          <p className="text-[10.5px] text-gray-400 font-mono">{r.razorpay_order_id}</p>
        </div>
      )
    },
    {
      key: 'customer', header: 'Customer', render: r => (
        <div>
          <p className="font-bold text-gray-900">{r.customer_name}</p>
          <p className="text-[11px] text-gray-400 font-medium">{r.customer_email}</p>
        </div>
      )
    },
    { key: 'plan', header: 'Plan', render: r => r.plan_name, hideBelow: 'md' },
    { key: 'method', header: 'Method', render: r => r.payment_method, hideBelow: 'lg' },
    { key: 'amount', header: 'Amount', render: r => formatAmount(r.amount), align: 'right' },
    { key: 'status', header: 'Status', render: r => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
    { key: 'date', header: 'Date', render: r => formatDateTime(r.date), hideBelow: 'md' },
    {
      key: 'actions', header: 'Action', align: 'right', render: r => (
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={e => { e.stopPropagation(); setSelectedId(r.payment_id); }}
            title="View details"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-navy hover:text-navy hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faEye} className="text-[12px]" />
          </button>
          <button
            onClick={e => { e.stopPropagation(); setInvoiceId(r.payment_id); }}
            title="View invoice"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-navy hover:text-navy hover:bg-gray-50 transition-colors"
          >
            <FontAwesomeIcon icon={faReceipt} className="text-[12px]" />
          </button>
          {r.refundable && (
            <button
              onClick={e => { e.stopPropagation(); setRefundTarget(r); }}
              title="Refund payment"
              className="w-7 h-7 flex items-center justify-center rounded-lg text-purple-600 hover:text-purple-600 hover:bg-purple-50 transition-colors"
            >
              <FontAwesomeIcon icon={faMoneyBillTransfer} className="text-[12px]" />
            </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div>
      <PageHeader title="Transaction History" subtitle="Every settled payment attempt across all subscribers — paid, failed, or refunded." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard label="Revenue" value={`₹${metrics.revenue.toLocaleString('en-IN')}`} icon={faWallet} iconBg="#f0fdf4" iconColor="#16a34a" />
        <StatCard label="Successful Payments" value={metrics.successful_payments} icon={faCircleCheck} iconBg="#eff6ff" iconColor="#2563eb" />
        <StatCard label="Failed Payments" value={metrics.failed_payments} icon={faCircleXmark} iconBg="#fef2f2" iconColor="#dc2626" />
        <StatCard label="Refunded" value={metrics.refunded} icon={faMoneyBillTransfer} iconBg="#f5f3ff" iconColor="#7c3aed" />
      </div>

      <Card>
        <CardHeader
          title="Transactions"
          subtitle="All completed payment attempts, newest first"
          action={<SearchInput value={search} onChange={setSearch} placeholder="Search payment ID, customer, email…" className="w-72" />}
        />
        <div className="flex gap-1.5 flex-wrap mb-4 -mt-1">
          {STATUS_TABS.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg capitalize transition-colors ${statusFilter === s ? 'bg-navy text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
            >
              {s}
            </button>
          ))}
        </div>
        <DataTable
          columns={columns}
          rows={txns}
          loading={loading}
          onRowClick={r => setSelectedId(r.payment_id)}
          emptyTitle="No transactions found"
          emptySubtitle="Try a different filter or search term."
        />
        <Pagination page={page} totalPages={totalPages} onChange={setPage} totalItems={totalCount} pageSize={PAGE_SIZE} />
      </Card>

      {/* Transaction details modal — fetches GET /admin/transactions/{payment_id}/ on open */}
      <Modal
        open={selectedId != null}
        onClose={() => setSelectedId(null)}
        title="Transaction details"
        footer={detail && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setSelectedId(null)}>Close</Button>
            {(txns.find(t => t.payment_id === detail.payment_id)?.refundable ?? detail.status === 'paid') && (
              <Button
                variant="secondary"
                size="sm"
                icon="ti-refresh"
                onClick={() => {
                  const row = txns.find(t => t.payment_id === detail.payment_id);
                  setRefundTarget(row ?? {
                    id: detail.payment_id,
                    s_no: 0,
                    payment_id: detail.payment_id,
                    razorpay_payment_id: detail.razorpay_payment_id,
                    razorpay_order_id: detail.razorpay_order_id,
                    customer_name: detail.customer_name,
                    customer_email: detail.customer_email,
                    plan_name: detail.plan_name,
                    payment_method: detail.payment_method,
                    amount: detail.amount,
                    status: detail.status,
                    date: detail.paid_at ?? detail.created_at,
                    has_invoice: !!detail.invoice,
                    refundable: true,
                  });
                  setSelectedId(null);
                }}
              >
                Refund
              </Button>
            )}
            <Button variant="primary" size="sm" icon="ti-receipt" onClick={() => { setInvoiceId(detail.payment_id); setSelectedId(null); }}>
              View invoice
            </Button>
          </>
        )}
      >
        {detailLoading && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <FontAwesomeIcon icon={faSpinner} spin className="text-[18px]" />
          </div>
        )}
        {!detailLoading && detail && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-navy text-white flex items-center justify-center font-black text-[14px] flex-shrink-0">
                {detail.customer_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="font-black text-gray-900 text-[14px]">{detail.customer_name}</p>
                <p className="text-[12px] text-gray-500">{detail.customer_email}</p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center justify-between gap-3">
              <FontAwesomeIcon icon={faReceipt} className="text-gray-300 text-[16px]" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-gray-700 font-mono truncate">{detail.razorpay_payment_id}</p>
                <p className="text-[10.5px] text-gray-400 font-mono truncate">{detail.razorpay_order_id}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              {[
                { label: 'Plan', value: detail.plan_name },
                { label: 'Status', value: <Badge tone={STATUS_TONE[detail.status]}>{detail.status}</Badge> },
                { label: 'Payment method', value: detail.payment_method },
                { label: 'Amount', value: formatAmount(detail.amount) },
                { label: 'Paid at', value: formatDateTime(detail.paid_at) },
                { label: 'Created at', value: formatDateTime(detail.created_at) },
                { label: 'Subscription ID', value: detail.subscription_id ?? '—' },
                { label: 'Invoice', value: detail.invoice ? 'Generated' : 'Not generated' },
              ].map(f => (
                <div key={f.label}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <div className="text-[13px] font-semibold text-gray-800">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Original invoice preview — fetches GET /admin/transactions/{payment_id}/
          and GET /admin/transactions/{payment_id}/invoice/ on open, and matches
          the printed/downloaded invoice layout 1:1. */}
      <Modal
        open={invoiceId != null}
        onClose={() => setInvoiceId(null)}
        title="Original Invoice"
        footer={invoiceDetail && (
          <>
            <Button variant="secondary" size="sm" onClick={() => setInvoiceId(null)}>Close</Button>
            <Button variant="primary" size="sm" icon="ti-printer" onClick={printInvoice}>
              <FontAwesomeIcon icon={faPrint} className="mr-1.5 text-[11px]" />
              Print / Download PDF
            </Button>
          </>
        )}
      >
        {invoiceLoading && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <FontAwesomeIcon icon={faSpinner} spin className="text-[18px]" />
          </div>
        )}
        {!invoiceLoading && invoiceDetail && (() => {
          const t = invoiceDetail;
          const gstPercent = invoiceInfo?.gst_percent ?? DEFAULT_GST_PERCENT;
          const computed = computeGstBreakdown(t.amount, gstPercent);
          const taxable = invoiceInfo?.taxable_amount ?? computed.taxable;
          const gst = invoiceInfo?.gst_amount ?? computed.gst;
          const total = invoiceInfo?.total_amount ?? computed.total;
          return (
            <div className="bg-white border border-gray-100 rounded-lg p-6">
              {/* Header */}
              <div className="flex items-start justify-between pb-5 mb-5">
                <div>
                  <img
                    src={LOGO_PATH}
                    alt="Manna Plag"
                    className="h-9 w-auto object-contain"
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                  <p className="text-[13px] font-black text-gray-900 mt-2.5">{COMPANY_NAME}</p>
                  <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">
                    {COMPANY_ADDRESS}<br />
                    {COMPANY_EMAIL}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[16px] font-black text-gray-900 tracking-tight">TAX INVOICE</p>
                  <p className="text-[12px] text-gray-500 mt-1">{formatDateTime(invoiceInfo?.invoice_date ?? t.paid_at ?? t.created_at)}</p>
                  <div className="mt-2">
                    <Badge tone={STATUS_TONE[t.status]}>{t.status}</Badge>
                  </div>
                </div>
              </div>

              {/* Meta table: billed to / payment id / order id */}
              <table className="w-full text-[12px] border border-gray-200 rounded-lg overflow-hidden mb-5" style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 p-3 align-top w-1/2">
                      <p className="text-[9.5px] font-bold text-gray-400 uppercase tracking-wide mb-1">Billed To</p>
                      <p className="font-bold text-gray-900">{t.customer_name}</p>
                      <p className="text-gray-500">{t.customer_email}</p>
                    </td>
                    <td className="border border-gray-200 p-3 align-top w-1/4">
                      <p className="text-[9.5px] font-bold text-gray-400 uppercase tracking-wide mb-1">Payment ID</p>
                      <p className="font-mono font-semibold text-gray-800 text-[11px]">{t.razorpay_payment_id}</p>
                    </td>
                    <td className="border border-gray-200 p-3 align-top w-1/4">
                      <p className="text-[9.5px] font-bold text-gray-400 uppercase tracking-wide mb-1">Order ID</p>
                      <p className="font-mono font-semibold text-gray-800 text-[11px]">{t.razorpay_order_id}</p>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Line items table */}
              <table className="w-full text-[12.5px] border border-gray-200 rounded-lg overflow-hidden" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 text-left py-2.5 px-3 font-bold text-gray-400 text-[10px] uppercase tracking-wide w-[6%]">#</th>
                    <th className="border border-gray-200 text-left py-2.5 px-3 font-bold text-gray-400 text-[10px] uppercase tracking-wide">Description</th>
                    <th className="border border-gray-200 text-left py-2.5 px-3 font-bold text-gray-400 text-[10px] uppercase tracking-wide w-[18%]">Method</th>
                    <th className="border border-gray-200 text-right py-2.5 px-3 font-bold text-gray-400 text-[10px] uppercase tracking-wide w-[22%]">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 py-2.5 px-3 text-gray-500">1</td>
                    <td className="border border-gray-200 py-2.5 px-3 text-gray-700">{t.plan_name} Plan — Subscription</td>
                    <td className="border border-gray-200 py-2.5 px-3 text-gray-700">{t.payment_method}</td>
                    <td className="border border-gray-200 py-2.5 px-3 text-right font-semibold text-gray-800">{formatMoney(taxable)}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 py-2 px-3"></td>
                    <td className="border border-gray-200 py-2 px-3 text-gray-500" colSpan={2}>Taxable Value</td>
                    <td className="border border-gray-200 py-2 px-3 text-right font-semibold text-gray-700">{formatMoney(taxable)}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 py-2 px-3"></td>
                    <td className="border border-gray-200 py-2 px-3 text-gray-500" colSpan={2}>GST ({gstPercent}%)</td>
                    <td className="border border-gray-200 py-2 px-3 text-right font-semibold text-gray-700">{formatMoney(gst)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 py-3 px-3"></td>
                    <td className="border border-gray-200 py-3 px-3 font-black text-gray-900" colSpan={2}>Total Amount</td>
                    <td className="border border-gray-200 py-3 px-3 text-right font-black text-gray-900">{formatMoney(total)}</td>
                  </tr>
                </tbody>
              </table>

              <p className="text-[10.5px] text-gray-400 mt-5 pt-4 border-t border-gray-100 leading-relaxed">
                This is a system-generated invoice and does not require a signature.<br />
                For queries regarding this invoice, contact {COMPANY_EMAIL}.
              </p>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog
        open={!!refundTarget}
        onClose={() => !refunding && setRefundTarget(null)}
        onConfirm={confirmRefund}
        title="Refund payment"
        message={refundTarget ? `Refund ${formatAmount(refundTarget.amount)} for ${refundTarget.razorpay_payment_id} (${refundTarget.customer_name})? This can't be undone.` : ''}
        confirmLabel="Refund"
        danger
        loading={refunding}
      />

      <Toast message={toast} />
    </div>
  );
}